import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useCallback } from 'react';
import useAsyncData from '../../shared/hooks/useAsyncData';
import { getModuleCards, type ModuleCard as ModuleCardData } from '../dashboards.api';
import ModuleCard, { ModuleCardSkeleton } from '../components/ModuleCard';

const EMPTY: ModuleCardData[] = [];

/** Home's top band: a card per module the signed-in user can open, each opening its dashboard. */
export default function HomeOverview() {
  const fetcher = useCallback(() => getModuleCards(), []);
  const { data, loading, error, refetch } = useAsyncData<ModuleCardData[]>(fetcher, EMPTY, {
    code: 'DASHBOARD_HOME_FAILED',
    errorMessage: 'Failed to load your overview',
    toastOnError: false,
  });

  return (
    <Stack gap={1.5} component='section' aria-label='Your overview'>
      <Typography component='h2' sx={{ fontSize: '1.125rem', fontWeight: 600 }}>
        Your overview
      </Typography>
      {error ? (
        <Alert severity='error' action={<Button onClick={refetch}>Retry</Button>}>
          {error}
        </Alert>
      ) : !loading && data.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          No module dashboards for your login yet. Use the menu on the left to reach your screens.
        </Typography>
      ) : (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))' }}>
          {loading
            ? [0, 1, 2].map((i) => <ModuleCardSkeleton key={i} />)
            : data.map((card) => <ModuleCard key={card.module} card={card} />)}
        </Box>
      )}
    </Stack>
  );
}
